/*
 * Copyright © 2020-2026 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 会员支付弹窗来源埋点上下文
 */
import {
  MEMBERSHIP_JOURNEY_VARIANTS,
  type MembershipJourneyVariant
} from '../../../watermark/batchEditor/model/membershipJourneyExperiment'
import {
  MEMBERSHIP_MODAL_VARIANTS,
  type MembershipModalVariant
} from '../../../watermark/batchEditor/model/membershipModalPresentation'
import type { MembershipPaywallGuideSource } from '../../../watermark/batchEditor/model/membershipPaywallGuide'

export const BATCH_WATERMARK_MEMBERSHIP_CONTEXT_VARIANT =
  'batchWatermarkMembershipExperiment'

export type BatchWatermarkPaymentPaywallSource =
  | Exclude<MembershipPaywallGuideSource, 'upload_and_delete'>
  | 'sync_to_workgroup'

export interface BatchWatermarkMembershipPaymentData {
  /** 本轮唯一实验变量：是否先展示会员引导。 */
  experimentVariant: MembershipJourneyVariant | MembershipModalVariant
  /** 会员引导内容版本，与旅程实验正交。 */
  presentationVersion?: MembershipModalVariant
  /** 当前任务推荐的最低会员档位。 */
  recommendedTier: string
  /** 触发购买的具体业务卡点。 */
  paywallSource?: BatchWatermarkPaymentPaywallSource
  /** 本次卡点涉及的照片数量。 */
  photoCount?: number
  /** 用户可见的目标会员名称。 */
  targetName?: string
  /** 当前购买动作。 */
  membershipAction?: 'open' | 'upgrade' | 'renew'
  /** 当前团队单次批量上限。 */
  currentBatchLimit?: number
  /** 目标会员单次批量上限。 */
  targetBatchLimit?: number
  /** 一次卡点到支付结果的稳定关联标识。 */
  paywallAttemptID?: string
  /** 工作圈同步任务标识。 */
  taskID?: string
  /** 批量编辑页面会话标识。 */
  sessionID?: string
}

interface PaymentTrackingCustomContent {
  variant: typeof BATCH_WATERMARK_MEMBERSHIP_CONTEXT_VARIANT
  data: BatchWatermarkMembershipPaymentData
}

interface BatchWatermarkMembershipTrackingParams {
  photoNum?: number
  string4?: string
  string5?: string
  string6?: string
  string7?: string
  string8?: string
  taskID?: string
  sessionID?: string
}

export type BatchWatermarkMembershipAttribution = Omit<
  BatchWatermarkMembershipPaymentData,
  'experimentVariant' | 'recommendedTier' | 'paywallSource'
> & {
  paywallSource?: MembershipPaywallGuideSource | 'sync_to_workgroup'
}

const PAYMENT_PAYWALL_SOURCES: BatchWatermarkPaymentPaywallSource[] = [
  'download',
  'upload',
  'sync_to_workgroup',
  'membership_rights',
  'experience_limit',
  'batch_limit'
]

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isMembershipJourneyVariant = (
  value: unknown
): value is MembershipJourneyVariant =>
  typeof value === 'string' &&
  MEMBERSHIP_JOURNEY_VARIANTS.some((variant) => variant === value)

const isMembershipModalVariant = (
  value: unknown
): value is MembershipModalVariant =>
  typeof value === 'string' &&
  MEMBERSHIP_MODAL_VARIANTS.some((variant) => variant === value)

const isPaymentPaywallSource = (
  value: unknown
): value is BatchWatermarkPaymentPaywallSource =>
  typeof value === 'string' &&
  PAYMENT_PAYWALL_SOURCES.some((source) => source === value)

const normalizePaywallSource = (
  source?: MembershipPaywallGuideSource | 'sync_to_workgroup'
): BatchWatermarkPaymentPaywallSource | undefined =>
  source === 'upload_and_delete' ? 'sync_to_workgroup' : source

/**
 * 创建批量编辑水印会员购买页的可序列化上下文。
 *
 * @param experimentVariant - 团队短 ID 固定命中的购买旅程
 * @param recommendedTier - 当前任务推荐的最低会员档位
 * @param attribution - 场景内容及支付全链路归因字段
 * @returns 可同时驱动支付内容和神策埋点的 customContent
 */
export const createBatchWatermarkMembershipTrackingContext = (
  experimentVariant: MembershipJourneyVariant | MembershipModalVariant,
  recommendedTier: string,
  attribution: BatchWatermarkMembershipAttribution = {}
): PaymentTrackingCustomContent => {
  const normalizedPaywallSource = normalizePaywallSource(
    attribution.paywallSource
  )

  return {
    variant: BATCH_WATERMARK_MEMBERSHIP_CONTEXT_VARIANT,
    data: {
      experimentVariant,
      recommendedTier,
      ...(attribution.presentationVersion
        ? { presentationVersion: attribution.presentationVersion }
        : {}),
      ...(normalizedPaywallSource
        ? { paywallSource: normalizedPaywallSource }
        : {}),
      ...(attribution.photoCount !== undefined
        ? { photoCount: attribution.photoCount }
        : {}),
      ...(attribution.targetName ? { targetName: attribution.targetName } : {}),
      ...(attribution.membershipAction
        ? { membershipAction: attribution.membershipAction }
        : {}),
      ...(attribution.currentBatchLimit !== undefined
        ? { currentBatchLimit: attribution.currentBatchLimit }
        : {}),
      ...(attribution.targetBatchLimit !== undefined
        ? { targetBatchLimit: attribution.targetBatchLimit }
        : {}),
      ...(attribution.paywallAttemptID
        ? { paywallAttemptID: attribution.paywallAttemptID }
        : {}),
      ...(normalizedPaywallSource === 'sync_to_workgroup' && attribution.taskID
        ? { taskID: attribution.taskID }
        : {}),
      ...(normalizedPaywallSource && attribution.sessionID
        ? { sessionID: attribution.sessionID }
        : {})
    }
  }
}

/**
 * 从支付弹窗 customContent 提取批量编辑水印会员实验参数。
 *
 * `string4-8` 依次为购买旅程、推荐档位、卡点来源、内容版本和卡点尝试 ID。
 * 旧内容版本仍可作为 `string4` 解析，避免发布切换时丢失在途支付归因。
 *
 * @param customContent - Redux paymentStatus 中的来源上下文
 * @returns 支付曝光、点击与结果事件共用的归因字段
 */
export const extractBatchWatermarkMembershipTrackingParams = (
  customContent: unknown
): BatchWatermarkMembershipTrackingParams => {
  if (!isRecord(customContent)) return {}
  if (customContent.variant !== BATCH_WATERMARK_MEMBERSHIP_CONTEXT_VARIANT) {
    return {}
  }

  const data = customContent.data
  if (!isRecord(data)) return {}

  const experimentVariant =
    isMembershipJourneyVariant(data.experimentVariant) ||
    isMembershipModalVariant(data.experimentVariant)
      ? data.experimentVariant
      : ''
  const presentationVersion = isMembershipModalVariant(data.presentationVersion)
    ? data.presentationVersion
    : ''
  const recommendedTier =
    typeof data.recommendedTier === 'string' ? data.recommendedTier.trim() : ''
  const paywallSource = isPaymentPaywallSource(data.paywallSource)
    ? data.paywallSource
    : ''
  const paywallAttemptID =
    typeof data.paywallAttemptID === 'string'
      ? data.paywallAttemptID.trim()
      : ''
  const taskID =
    paywallSource === 'sync_to_workgroup' && typeof data.taskID === 'string'
      ? data.taskID.trim()
      : ''
  const sessionID =
    paywallSource && typeof data.sessionID === 'string'
      ? data.sessionID.trim()
      : ''
  const photoNum =
    typeof data.photoCount === 'number' && Number.isFinite(data.photoCount)
      ? Math.max(0, Math.floor(data.photoCount))
      : undefined

  return {
    ...(experimentVariant ? { string4: experimentVariant } : {}),
    ...(recommendedTier ? { string5: recommendedTier } : {}),
    ...(paywallSource ? { string6: paywallSource } : {}),
    ...(presentationVersion ? { string7: presentationVersion } : {}),
    ...(paywallAttemptID ? { string8: paywallAttemptID } : {}),
    ...(photoNum !== undefined ? { photoNum } : {}),
    ...(taskID ? { taskID } : {}),
    ...(sessionID ? { sessionID } : {})
  }
}
